import { Cesium3DTileFeature, Viewer, Cesium3DTileset, Resource } from "cesium";
import { DeferredValue, Guard } from "lib/actions/util";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { ActivatableToolInterface } from "./ActivatableToolInterface";
import TilesetStylingPlugin, {
  StyleRule,
  TilesetStylePriority,
} from "./TilesetStylingPlugin";
import TilesetPlugin from "./TilesetPlugin";
import { getElementTilesetFileName } from "../utils/getElementTilesetFileName";
import FeatureRayPickerPlugin from "components/elements/CesiumViewer/core/wrapper/FeatureRayPickerPlugin";
import { ObservableProperty } from "components/elements/CesiumViewer/core/wrapper/ObservableProperty";
import { parseContentUri } from "lib/actions/assets/parseContentUri";
import { AzureStorageResource } from "components/elements/CesiumViewer/core/wrapper/AzureStorageResource";
import TransformEditorPlugin, {
  TransformEditorElementState,
} from "components/elements/CesiumViewer/core/wrapper/TransformEditorPlugin";

export type RepositionedElement = {
  modelTilesetId: string;
  elementTilesetId: string;
  elementId: string | null;
  state?: TransformEditorElementState;
};

type StateSnapshot = {
  repositionedElements: RepositionedElement[];
};

export default class TilesetElementRepositionPlugin
  implements AttachablePluginInterface, ActivatableToolInterface
{
  private _viewer = new DeferredValue<Viewer>();
  private _isActive = false;
  private _projectId: number | undefined;

  private readonly _repositionedElements = new ObservableProperty<
    RepositionedElement[]
  >([]);
  private readonly _currentSelection =
    new ObservableProperty<RepositionedElement | null>(null);

  private _detachClickHandlerFunc: () => void;
  private _detachEmptyClickHandlerFunc: () => void;

  private static readonly SELECTED_COLOR = "FUCHSIA";
  private static readonly UNSELECTED_COLOR = "LIGHTSEAGREEN";
  private static readonly HIDDEN_ELEMENT_COLOR = "#FF0000";
  private static readonly HIDDEN_ELEMENT_OPACITY = 0.05;

  constructor(
    private readonly _tilesetPlugin: TilesetPlugin,
    private readonly _tilesetStylingPlugin: TilesetStylingPlugin,
    private readonly _featureRayPickerPlugin: FeatureRayPickerPlugin,
    private readonly _transformEditorPlugin: TransformEditorPlugin,
  ) {}

  setViewer(viewer: Viewer): void {
    this._viewer.set(viewer);
  }

  detachViewer(): void {
    this._viewer.clear();
  }

  get repositionedElements() {
    return this._repositionedElements;
  }

  get currentSelection() {
    return this._currentSelection;
  }

  isActive(): boolean {
    return this._isActive;
  }

  activate(): void {
    this._isActive = true;
    this.setInteractiveMode(true);
  }

  deactivate(): void {
    this._isActive = false;
    this.setInteractiveMode(false);
  }

  setInteractiveMode(isEnabled: boolean) {
    if (isEnabled) {
      this.attachLeftClickHandler();
    } else {
      this._detachClickHandlerFunc?.();
      this._detachClickHandlerFunc = null;
      this._detachEmptyClickHandlerFunc?.();
      this._detachEmptyClickHandlerFunc = null;
      this._transformEditorPlugin.destroy();
      this.removeElementTilesetHighlight(
        this._currentSelection.getSnapshot()?.elementTilesetId,
      );
      this._currentSelection.set(null);
    }
  }

  setCurrentProject(projectId: number) {
    this._projectId = projectId;
  }

  async clearCurrentElementTileset(): Promise<void> {
    const selection = this._currentSelection.getSnapshot();
    if (selection) {
      this._transformEditorPlugin.deactivate();

      const viewer = await this._viewer.getValueWhenReady();
      if (viewer) {
        this._tilesetPlugin.hide(selection.elementTilesetId);
      }
      this.resetTilesetElementStyling(selection);
      const currentRepositionedElements =
        this._repositionedElements.getSnapshot();
      this._repositionedElements.set(
        currentRepositionedElements.filter(
          (e) => e.elementTilesetId !== selection.elementTilesetId,
        ),
      );
      this._currentSelection.set(null);
    }
  }

  async resetAllElements(): Promise<void> {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    const repositionedElements = this._repositionedElements.getSnapshot();
    repositionedElements.forEach((element) => {
      this._tilesetPlugin.hide(element.elementTilesetId);
      this.resetTilesetElementStyling(element);
    });

    this._repositionedElements.set([]);
    this._currentSelection.set(null);
    this._transformEditorPlugin.deactivate();
  }

  applyState(state: object) {
    if (!state) return;
    const { repositionedElements } = state as StateSnapshot;

    repositionedElements.forEach((e) => {
      (async () => {
        const isLoaded = await this.findTilesetById(e.modelTilesetId);
        if (!isLoaded) return;

        await this.addTileset(e.elementTilesetId, this._projectId);

        const repositionedElement = e as RepositionedElement;

        this._repositionedElements.set([
          ...this._repositionedElements.getSnapshot(),
          repositionedElement,
        ]);
      })();
    });
  }

  captureState(): object {
    const state: StateSnapshot = {
      repositionedElements: [...this._repositionedElements.getSnapshot()],
    };
    return state;
  }

  private attachLeftClickHandler() {
    this._detachClickHandlerFunc?.();
    this._detachEmptyClickHandlerFunc?.();
    this._detachClickHandlerFunc =
      this._featureRayPickerPlugin.leftClickFeatureEvent.addHandler(
        (object) => {
          // Not TilesetPlugin.isFeature: only mesh features can be repositioned.
          if (!(object instanceof Cesium3DTileFeature)) return;

          (async () => {
            const tilesetId = TilesetPlugin.getTilesetIdFromTileset(
              object.tileset,
            );
            const isElementTileset = this._repositionedElements
              .getSnapshot()
              .some(
                (repositionedElement) =>
                  repositionedElement.elementTilesetId === tilesetId,
              );

            if (isElementTileset) {
              this.onClickElementTileset(object.tileset);
            } else {
              await this.onClickModelTilesetFeature(object);
            }
          })();
        },
      );

    this._detachEmptyClickHandlerFunc =
      this._featureRayPickerPlugin.leftClickEmptyEvent.addHandler(() => {
        const selection = this._currentSelection.getSnapshot();
        if (selection) {
          this.removeElementTilesetHighlight(selection.elementTilesetId);
          this._currentSelection.set(null);
          this._transformEditorPlugin.deactivate();
        }
      });
  }

  private selectElementTileset(element: RepositionedElement): void {
    const oldSelection = this._currentSelection.getSnapshot();
    if (oldSelection && oldSelection !== element) {
      this.removeElementTilesetHighlight(oldSelection.elementTilesetId);
    }

    this.highlightSelectedElementTileset(element.elementTilesetId);
    this._currentSelection.set(element);

    const elementTileset = this._tilesetPlugin.get(element.elementTilesetId);
    this._transformEditorPlugin.initializeForTileset(
      elementTileset,
      element.state,
    );
    this._transformEditorPlugin.setActive(true);
  }

  private async onClickModelTilesetFeature(feature: Cesium3DTileFeature) {
    const elementId = TilesetPlugin.getElementIdFromFeature(feature);
    const modelTilesetId = TilesetPlugin.getTilesetIdFromTileset(
      feature.tileset,
    );

    Guard.require(elementId, "elementId");
    Guard.require(modelTilesetId, "modelTilesetId");

    const elementTilesetId = await this.getElementTilesetUri(
      modelTilesetId,
      elementId,
    );

    const existingElementTileset = this._repositionedElements
      .getSnapshot()
      .find((e) => e.elementTilesetId === elementTilesetId);

    if (existingElementTileset) {
      this.selectElementTileset(existingElementTileset);
      return;
    }

    await this.addTileset(elementTilesetId, this._projectId);

    const elementTileset = this._tilesetPlugin.get(elementTilesetId);
    if (!elementTileset) {
      console.error("Failed to load element tileset");
      return;
    }

    const selection: RepositionedElement = {
      modelTilesetId,
      elementTilesetId,
      elementId,
      state: {
        initialModelMatrix: elementTileset.modelMatrix.clone(),
        initialBoundingSphereCenter:
          elementTileset.boundingSphere.center.clone(),
      },
    };

    this._repositionedElements.set([
      ...this._repositionedElements.getSnapshot(),
      selection,
    ]);

    this.selectElementTileset(selection);
    await this.hideTilesetElement(modelTilesetId, elementId);
  }

  private onClickElementTileset(tileset: Cesium3DTileset) {
    const repositionedElements = this._repositionedElements.getSnapshot();
    const tilesetId = tileset.extras?.tilesetId;
    if (!tilesetId) return;

    const existingTileset = repositionedElements.find(
      (t) => t.elementTilesetId == tilesetId,
    );

    if (!existingTileset) return;

    this.selectElementTileset(existingTileset);
  }

  private async findTilesetById(tilesetId: string) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return null;
    const primitives = viewer.scene.primitives;
    for (let index = 0; index < primitives.length; index++) {
      const tileset = primitives.get(index) as Cesium3DTileset;
      if (tileset?.extras?.tilesetId === tilesetId) return tileset;
    }
    return null;
  }

  private async addTileset(
    contentUri: string,
    projectId: number,
  ): Promise<void> {
    // AutoCAD layer group ids contain '#', which would otherwise end the path.
    // Only the request is encoded, since contentUri also identifies the element
    // tileset in saved reposition state.
    const lastSeparatorIndex = contentUri.lastIndexOf("/");
    const resourcePath =
      contentUri.slice(0, lastSeparatorIndex + 1) +
      encodeURIComponent(contentUri.slice(lastSeparatorIndex + 1));
    const resource: Resource = await AzureStorageResource.fromUrl(
      resourcePath,
      projectId,
    );

    await this._tilesetPlugin.load(contentUri, resource);

    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    const selection = this._currentSelection.getSnapshot();
    if (selection) {
      this.removeElementTilesetHighlight(selection.elementTilesetId);
    }
  }

  private async getElementTilesetUri(tilesetId: string, elementId: string) {
    const contentUri = parseContentUri(tilesetId).value;
    const pathSegments = contentUri.split("/");
    pathSegments.pop();
    const basePath = pathSegments.join("/");
    const uri =
      basePath +
      "/elements/exploded/" +
      (await getElementTilesetFileName(elementId));
    return uri;
  }

  private async resetTilesetElementStyling(
    currentSelection: RepositionedElement,
  ) {
    if (!currentSelection || currentSelection.elementId === null) return;

    const oldRule: StyleRule = {
      priority: TilesetStylePriority.ElementSelection,
      elementStyle: {
        tilesetId: currentSelection.modelTilesetId,
        elementId: currentSelection.elementId,
        color: TilesetElementRepositionPlugin.HIDDEN_ELEMENT_COLOR,
        opacity: TilesetElementRepositionPlugin.HIDDEN_ELEMENT_OPACITY,
      },
    };

    this._tilesetStylingPlugin.removeRule(oldRule);
    this._tilesetStylingPlugin.applyRules();
  }

  private async hideTilesetElement(tilesetId: string, elementId: string) {
    if (!elementId) return;
    const rule: StyleRule = {
      priority: TilesetStylePriority.ElementSelection,
      elementStyle: {
        tilesetId: tilesetId,
        elementId: elementId,
        color: TilesetElementRepositionPlugin.HIDDEN_ELEMENT_COLOR,
        opacity: TilesetElementRepositionPlugin.HIDDEN_ELEMENT_OPACITY,
      },
    };
    this._tilesetStylingPlugin.setRule(rule);
    await this._tilesetStylingPlugin.applyRules();
  }

  private async highlightSelectedElementTileset(elementTilesetId: string) {
    if (!elementTilesetId) return;
    const rule: StyleRule = {
      priority: TilesetStylePriority.PaintToolTileset,
      tilesetStyle: {
        tilesetId: elementTilesetId,
        color: TilesetElementRepositionPlugin.SELECTED_COLOR,
        opacity: 0.5,
      },
    };
    this._tilesetStylingPlugin.setRule(rule);
    await this._tilesetStylingPlugin.applyRules();
  }

  private async removeElementTilesetHighlight(elementTilesetId: string) {
    if (!elementTilesetId) return;
    const oldRule: StyleRule = {
      priority: TilesetStylePriority.ElementSelection,
      tilesetStyle: {
        tilesetId: elementTilesetId,
        color: TilesetElementRepositionPlugin.SELECTED_COLOR,
        opacity: 0.5,
      },
    };
    const newRule: StyleRule = {
      priority: TilesetStylePriority.PaintToolTileset,
      tilesetStyle: {
        tilesetId: elementTilesetId,
        color: TilesetElementRepositionPlugin.UNSELECTED_COLOR,
      },
    };
    this._tilesetStylingPlugin.removeRule(oldRule);
    await this._tilesetStylingPlugin.applyRules();
    this._tilesetStylingPlugin.setRule(newRule);
    await this._tilesetStylingPlugin.applyRules();
  }
}
